import usePageMeta from "@/lib/usePageMeta";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

function LegalLayout({ overline, title, children, testid }) {
  return (
    <main>
      <section className="relative overflow-hidden pb-24 pt-40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.08), transparent 55%)" }} />
        <div className="relative mx-auto max-w-3xl px-6">
          <SectionHead overline={overline} title={title} testid={testid} />
          <Reveal delay={0.1}>
            <div className="space-y-8 text-sm leading-relaxed text-slate-400">{children}</div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h3 className="mb-2.5 font-display text-lg font-semibold text-white">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

export function PrivacyPolicy() {
  usePageMeta("Privacy Policy — Talentify", "How Talentify collects, uses and protects personal information shared through this website.");
  return (
    <LegalLayout overline="Legal" title="Privacy Policy" testid="privacy-heading">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Last updated: January 2026</p>
      <Block title="1. Information We Collect">
        When you submit a form on this website — a hiring requirement, a resume, or a contact message — we collect the information you provide, such as your name, company, email address, phone number, location, professional experience and resume documents.
      </Block>
      <Block title="2. How We Use Your Information">
        We use this information solely to provide recruitment and talent acquisition services: responding to hiring requirements, evaluating candidate profiles, coordinating interviews and communicating about relevant opportunities. We do not sell your personal information to third parties.
      </Block>
      <Block title="3. Candidate Data & Confidentiality">
        Candidate profiles and resumes are handled with strict confidentiality and are shared with prospective employers only in the context of relevant opportunities. Employer requirements and hiring information are treated with the same discretion.
      </Block>
      <Block title="4. Data Security">
        We apply reasonable technical and organisational measures to protect personal information against unauthorised access, alteration or disclosure. Resume files and form submissions are stored securely on our systems.
      </Block>
      <Block title="5. Data Retention">
        We retain personal information only for as long as necessary to fulfil recruitment purposes or as required by applicable law. You may request access, correction or deletion of your personal data at any time by writing to hello@talentifyhs.com.
      </Block>
      <Block title="6. Third-Party Services">
        This website may use standard analytics and infrastructure services to operate and improve the site. These services process limited technical data under their own privacy terms.
      </Block>
      <Block title="7. Contact">
        For any privacy-related questions or requests, contact us at hello@talentifyhs.com or +91 70161 20977.
      </Block>
    </LegalLayout>
  );
}

export function TermsConditions() {
  usePageMeta("Terms & Conditions — Talentify", "Terms and conditions governing the use of the Talentify website and recruitment services.");
  return (
    <LegalLayout overline="Legal" title="Terms & Conditions" testid="terms-heading">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Last updated: January 2026</p>
      <Block title="1. About These Terms">
        These terms govern the use of the Talentify website and the recruitment and talent acquisition services offered through it. By using this website or submitting information through its forms, you accept these terms.
      </Block>
      <Block title="2. Services">
        Talentify provides recruitment support including IT recruitment, Non-IT recruitment, leadership hiring, volume hiring and end-to-end recruitment across PAN India. Specific engagement terms, timelines and fees are agreed separately with each client in writing.
      </Block>
      <Block title="3. Employer Obligations">
        Employers submitting requirements agree to provide accurate role and organisational information, to engage with presented candidates professionally, and to honour the confidentiality of candidate information shared with them.
      </Block>
      <Block title="4. Candidate Obligations">
        Candidates submitting profiles confirm that the information and documents provided are accurate, current and their own. Submission of a resume does not guarantee an interview, shortlist or placement.
      </Block>
      <Block title="5. Intellectual Property">
        All content on this website — including text, design, graphics and the Talentify brand — is the property of Talentify and may not be reproduced without written permission.
      </Block>
      <Block title="6. Limitation of Liability">
        While we apply rigorous screening and structured processes, hiring decisions rest solely with the employer, and career decisions rest solely with the candidate. Talentify is not liable for decisions made by either party based on information presented through our services.
      </Block>
      <Block title="7. Governing Law">
        These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of India.
      </Block>
      <Block title="8. Contact">
        For questions about these terms, contact us at hello@talentifyhs.com or +91 70161 20977.
      </Block>
    </LegalLayout>
  );
}
